import { lazy, Suspense } from "react";
import { FilePlus, LayoutDashboard } from "lucide-react";
import { useMe, useTree } from "@/lib/queries";
import { openNote, openView, type Tab } from "@/lib/store";
import type { Workspace } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { NoteView } from "@/features/notes/NoteView";
import { KindIcon } from "@/features/notes/kinds";
import { post } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/queries";
import { createNoteIn } from "@/features/crypto/createNote";
import { ProjectGate } from "@/features/crypto/ProjectGate";
import { NoteTitle } from "@/features/crypto/UnlockPanel";

const LearningView = lazy(() => import("@/features/learning/LearningView").then((m) => ({ default: m.LearningView })));
const PaperView = lazy(() => import("@/features/library/PaperView").then((m) => ({ default: m.PaperView })));
const LibraryView = lazy(() => import("@/features/library/LibraryView").then((m) => ({ default: m.LibraryView })));
const MatrixView = lazy(() => import("@/features/library/MatrixView").then((m) => ({ default: m.MatrixView })));
const CodeDataView = lazy(() => import("@/features/code-data/CodeDataView").then((m) => ({ default: m.CodeDataView })));
const KnowledgeView = lazy(() => import("@/features/knowledge/KnowledgeView").then((m) => ({ default: m.KnowledgeView })));
const BacklogView = lazy(() => import("@/features/tasks/BacklogView").then((m) => ({ default: m.BacklogView })));
const ManuscriptView = lazy(() => import("@/features/manuscripts/ManuscriptView").then((m) => ({ default: m.ManuscriptView })));
const OverviewView = lazy(() => import("@/features/overview/OverviewView").then((m) => ({ default: m.OverviewView })));
const ReviewView = lazy(() => import("@/features/overview/ReviewView").then((m) => ({ default: m.ReviewView })));
const FilesView = lazy(() => import("@/features/files/FilesView").then((m) => ({ default: m.FilesView })));
const CorrespondenceView = lazy(() => import("@/features/correspondence/CorrespondenceView").then((m) => ({ default: m.CorrespondenceView })));
const SpaceSettingsView = lazy(() => import("@/features/settings/SpaceSettingsView").then((m) => ({ default: m.SpaceSettingsView })));
const TrashView = lazy(() => import("@/features/notes/TrashView").then((m) => ({ default: m.TrashView })));

function EmptyState({ workspace }: { workspace?: Workspace }) {
  const tree = useTree(workspace?.id);
  const qc = useQueryClient();
  if (!workspace)
    return (
      <div className="mx-auto max-w-2xl p-8">
        <h1 className="font-display text-[1.7rem] leading-tight font-semibold">Nothing here yet</h1>
        <p className="mt-1 text-sm text-muted-foreground">Create a notebook or project with “New notebook” or “New project” on the left.</p>
      </div>
    );
  const recent = [...(tree.data?.notes ?? [])].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)).slice(0, 8);
  return (
    <div className="mx-auto max-w-2xl p-8">
      <p className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {workspace.kind === "notebook" ? "Notebook" : "Research project"} · {workspace.memberCount > 1 ? `shared with ${workspace.memberCount - 1} other${workspace.memberCount === 2 ? "" : "s"}` : "private"}
      </p>
      <h1 className="font-display mt-1 text-[1.7rem] leading-tight font-semibold sm:text-[2rem]">{workspace.name}</h1>
      <p className="mt-1 text-sm text-muted-foreground">Open a note from the browser, or start something new.</p>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button
          onClick={async () => {
            const note = await createNoteIn(workspace, { title: "Untitled" });
            await qc.invalidateQueries({ queryKey: qk.tree(workspace.id) });
            openNote(note.id, note.title, workspace.id);
          }}
        >
          <FilePlus /> New blank note
        </Button>
        {workspace.kind === "project" && (
          <Button variant="outline" onClick={() => openView("overview", workspace.id, "Overview")}>
            <LayoutDashboard /> Project overview
          </Button>
        )}
      </div>
      {recent.length > 0 && (
        <section className="mt-8">
          <h2 className="mb-2 text-sm font-semibold text-muted-foreground">Recently updated</h2>
          <ul className="divide-y rounded-md border">
            {recent.map((n) => (
              <li key={n.id}>
                <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent" onClick={() => openNote(n.id, n.title, workspace.id)}>
                  <KindIcon kind={n.kind} />{" "}
                  <span className="truncate">
                    <NoteTitle note={n} />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export function ViewHost({ tab, workspace }: { tab: Tab | null; workspace?: Workspace }) {
  const me = useMe();
  if (!me.data) return null;
  if (!tab)
    return (
      <ProjectGate workspaceId={workspace?.id}>
        <EmptyState workspace={workspace} />
      </ProjectGate>
    );
  const fallback = <p className="p-6 text-muted-foreground" role="status">Loading…</p>;
  let content: React.ReactNode = null;
  if (tab.kind === "note" && tab.id) content = <NoteView key={tab.id} noteId={tab.id} user={me.data} workspace={workspace} />;
  else if (tab.kind === "paper" && tab.id) content = <PaperView key={tab.id} itemId={tab.id} page={tab.page} tabKey={tab.key} />;
  else if (tab.kind === "view") {
    const ws = tab.workspaceId ?? workspace?.id;
    switch (tab.view) {
      case "overview":
        content = ws && <OverviewView workspaceId={ws} />;
        break;
      case "library":
        content = ws && <LibraryView workspaceId={ws} />;
        break;
      case "matrix":
        content = ws && <MatrixView workspaceId={ws} />;
        break;
      case "code_data":
        content = ws && <CodeDataView workspaceId={ws} />;
        break;
      case "knowledge":
        content = ws && <KnowledgeView workspaceId={ws} />;
        break;
      case "backlog":
        content = ws && <BacklogView workspaceId={ws} highlightTaskId={tab.params?.task} />;
        break;
      case "manuscript":
        content = tab.id && <ManuscriptView manuscriptId={tab.id} tabKey={tab.key} params={tab.params} />;
        break;
      case "review":
        content = ws && <ReviewView workspaceId={ws} />;
        break;
      case "files":
        content = ws && <FilesView workspaceId={ws} />;
        break;
      case "correspondence":
        content = ws && <CorrespondenceView workspaceId={ws} initialThread={tab.params?.thread} addNonce={tab.params?.add} />;
        break;
      case "settings":
        content = ws && <SpaceSettingsView workspaceId={ws} section={tab.params?.section} />;
        break;
      case "trash":
        content = ws && <TrashView workspaceId={ws} />;
        break;
      case "learning":
        content = tab.id && <LearningView key={tab.id} sessionId={tab.id} />;
        break;
      default:
        content = <p className="p-6">This page could not be opened. Close the tab and try again.</p>;
    }
  }
  // Views of an encrypted project open only once it is unlocked (notes are gated by NoteView).
  // Its settings stay reachable while it is locked: unlocking and sharing are done there.
  const gateWs = tab.kind === "view" && tab.view !== "settings" ? (tab.workspaceId ?? workspace?.id) : tab.kind === "paper" ? tab.workspaceId : undefined;
  return (
    <Suspense fallback={fallback}>
      <div className="h-full min-h-0 overflow-hidden" key={tab.key}>
        <ProjectGate workspaceId={gateWs}>{content}</ProjectGate>
      </div>
    </Suspense>
  );
}
